import { BaseController, type SubtreeModel } from "subtree.js/core";
import type { AppDeps } from "../../../../app-deps";
import {
  attachStripeAccount,
  connectStripe,
  disconnectProvider,
  loadProviders,
  refreshProvider,
  stripeDashboardLink,
} from "../../../../core/ops/provider-ops";
import { errorText } from "../../../../core/primitives/errors";
import { loaded, loading } from "../../../../core/primitives/load-status";
import { ProvidersSectionActions, ProvidersSectionState } from "./providers-section-model";

export type ProvidersSectionDeps = Pick<AppDeps, "client" | "authHandler" | "providersRepository" | "storeRepository">;

/** A browser tab opened on the click, pointed somewhere once the URL is known. */
export interface ExternalTab {
  navigate(url: string): void;
  close(): void;
}

export interface ProvidersSectionHost {
  readonly storeId: string;
  /** Null when a pop-up blocker refused; the caller then uses the current tab. */
  openBlankTab(): ExternalTab | null;
  openExternal(url: string): void;
}

/**
 * The shop's commerce and payment connections. The list is the repository the
 * shell keeps fresh; every action here marks it stale through its op.
 */
export class ProvidersSectionController extends BaseController implements ProvidersSectionActions {
  readonly state = new ProvidersSectionState();
  #known = false;

  constructor(
    subtree: SubtreeModel,
    readonly deps: ProvidersSectionDeps,
    readonly host: ProvidersSectionHost,
  ) {
    super();
    subtree.put(ProvidersSectionState, this.state);
    subtree.put(ProvidersSectionActions, this);
    this.sync(() => {
      const repo = deps.providersRepository;
      const ours = repo.storeId === host.storeId;
      if (ours && repo.status.kind === "loaded") this.#known = true;
      this.state.providers.value = ours ? repo.providers : [];
      this.state.stripeTestMode.value = ours && repo.stripeMode === "test";
      if (this.#known) this.state.status.value = loaded;
      else if (ours && repo.status.kind === "error") this.state.status.value = repo.status;
      else this.state.status.value = loading;
    }, [deps.providersRepository]);
  }

  connect = async (providerKey: string) => {
    if (this.state.connectingKey.value) return;
    this.state.actionError.value = null;
    this.state.connectingKey.value = providerKey;
    const r = await connectStripe({ ...this.deps, storeId: this.host.storeId });
    if (this.isDisposed) return;
    if (!r.ok) {
      this.state.actionError.value = { providerKey, message: errorText(r.errors) };
      this.state.connectingKey.value = null;
      return;
    }
    this.host.openExternal(r.value.url);
  };

  /**
   * Stripe hosts the merchant's view of their own money, so this opens their
   * Stripe dashboard. The tab is opened on the click and pointed at the URL once
   * it arrives: the URL is resolved server-side per click, and a `window.open`
   * from inside the promise would be a pop-up blocker's textbook target.
   */
  openDashboard = async () => {
    if (this.state.dashboardBusy.value) return;
    this.state.actionError.value = null;
    this.state.dashboardBusy.value = true;
    const tab = this.host.openBlankTab();
    const r = await stripeDashboardLink({ ...this.deps, storeId: this.host.storeId });
    if (this.isDisposed) return;
    this.state.dashboardBusy.value = false;
    if (!r.ok) {
      tab?.close();
      this.state.actionError.value = { providerKey: "stripe", message: errorText(r.errors) };
      return;
    }
    // Pop-up blocked: this is a deliberate click, so use the current tab rather than dropping a single-use link.
    if (tab) tab.navigate(r.value.url);
    else this.host.openExternal(r.value.url);
  };

  setAttachAccountId = (value: string) => {
    this.state.attachAccountId.value = value;
  };

  attachAccount = async () => {
    const accountId = this.state.attachAccountId.value.trim();
    if (this.state.attaching.value || !accountId) return;
    this.state.actionError.value = null;
    this.state.attaching.value = true;
    const r = await attachStripeAccount({ ...this.deps, storeId: this.host.storeId, accountId });
    if (this.isDisposed) return;
    this.state.attaching.value = false;
    if (!r.ok) {
      this.state.actionError.value = { providerKey: "stripe", message: errorText(r.errors) };
      return;
    }
    this.state.attachAccountId.value = "";
  };

  refresh = async (providerKey: string) => {
    if (this.state.refreshingKey.value) return;
    this.state.actionError.value = null;
    this.state.refreshingKey.value = providerKey;
    const r = await refreshProvider({ ...this.deps, storeId: this.host.storeId, providerKey });
    if (this.isDisposed) return;
    this.state.refreshingKey.value = null;
    if (!r.ok) this.state.actionError.value = { providerKey, message: errorText(r.errors) };
  };

  startDisconnect = (providerKey: string) => {
    this.state.confirmingDisconnect.value = providerKey;
  };

  cancelDisconnect = () => {
    this.state.confirmingDisconnect.value = null;
  };

  disconnect = async (providerKey: string) => {
    if (this.state.disconnectingKey.value) return;
    this.state.actionError.value = null;
    this.state.disconnectingKey.value = providerKey;
    const r = await disconnectProvider({ ...this.deps, storeId: this.host.storeId, providerKey });
    if (this.isDisposed) return;
    this.state.disconnectingKey.value = null;
    if (!r.ok) {
      this.state.actionError.value = { providerKey, message: errorText(r.errors) };
      return;
    }
    this.state.confirmingDisconnect.value = null;
  };

  retry = () => {
    void loadProviders({ ...this.deps, storeId: this.host.storeId });
  };
}
